import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { request, tokens } from "./api";
import { ADMIN_ROLES, STUDENT_ROLES, type User } from "./types";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => tokens.getUser<User>());
  const [loading, setLoading] = useState(true);

  // On mount, verify the token is still valid.
  useEffect(() => {
    const tok = tokens.getAccess();
    if (!tok) {
      setLoading(false);
      return;
    }
    request<User>("/auth/me")
      .then((u) => {
        setUser(u);
        tokens.setUser(u);
      })
      .catch(() => {
        tokens.clear();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const pair = await request<{ access_token: string; refresh_token: string }>("/auth/login", {
      method: "POST",
      body: { email, password },
      skipAuth: true,
    });
    tokens.setTokens(pair.access_token, pair.refresh_token);
    const u = await request<User>("/auth/me");
    tokens.setUser(u);
    setUser(u);
    return u;
  };

  const logout = () => {
    tokens.clear();
    setUser(null);
  };

  const refresh = async () => {
    const u = await request<User>("/auth/me");
    tokens.setUser(u);
    setUser(u);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

type RoleGroup = "admin" | "student" | "any";

export function ProtectedRoute({
  children,
  require = "any",
}: {
  children: ReactNode;
  require?: RoleGroup;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <div className="text-muted">Se încarcă…</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Admins can preview student pages; students cannot access admin pages.
  if (require === "admin" && !ADMIN_ROLES.includes(user.role)) {
    return <Navigate to="/student/dashboard" replace />;
  }
  if (require === "student" && !STUDENT_ROLES.includes(user.role) && !ADMIN_ROLES.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export function isAdmin(user: User | null) {
  return user != null && ADMIN_ROLES.includes(user.role);
}

export function isStudent(user: User | null) {
  return user != null && STUDENT_ROLES.includes(user.role);
}
